import { useMutation } from "@apollo/client/react";
import { Box, IconicButton, TruncatedText } from "@nulogy/components";
import { GridBoxPlannedBreaks, StyledText } from "components/common/EditableListStyledComponents";
import FormWithInlineErrors from "components/controls/FormWithInlineErrors";
import EditablePlannedBreakRow from "components/pages/plannedBreaks/EditablePlannedBreakRow";
import { UpdatePlannedBreak } from "hooks/apollo/plannedBreak/plannedBreak.gql";
import React, { useState } from "react";
import DateTime from "utils/DateTime";
import Time from "utils/Time";

function displayNameForDay(day) {
  const map = {
    friday: "Fri",
    monday: "Mon",
    saturday: "Sat",
    sunday: "Sun",
    thursday: "Thu",
    tuesday: "Tue",
    wednesday: "Wed",
  };

  return map[day];
}

export default function PlannedBreak({ plannedBreak, onDelete, allLinesCount }) {
  const [editing, setEditing] = useState(false);
  const [updatePlannedBreak] = useMutation(UpdatePlannedBreak, { onCompleted: () => setEditing(false) });

  const { id, name, startTime, endTime, lines } = plannedBreak;
  const formattedStartTime = Time.to12Hour(startTime);
  const formattedEndTime = Time.to12Hour(endTime);
  const days = [];
  const lineText =
    lines.length === allLinesCount ? "All lines are selected." : lines.map((line) => line.name).join(", ");
  const lineIds = lines.map((line) => line.id);

  DateTime.DAYS_OF_WEEK.forEach((day) => {
    if (plannedBreak[day] === true) {
      days.push(day);
    }
  });

  const handleCancel = (onReset) => {
    onReset();
    setEditing(false);
  };

  const handleSave = (updatedPlannedBreak) => {
    const updatedDays = {};
    DateTime.DAYS_OF_WEEK.forEach((day) => {
      updatedDays[day] = updatedPlannedBreak.days.includes(day);
    });
    const updatedStartTime = Time.to24Hour(updatedPlannedBreak.startTime);
    const updatedEndTime = Time.to24Hour(updatedPlannedBreak.endTime);

    return updatePlannedBreak({
      variables: {
        endTime: updatedEndTime,
        id: updatedPlannedBreak.id,
        lineIds: updatedPlannedBreak.lineIds,
        name: updatedPlannedBreak.name,
        startTime: updatedStartTime,
        ...updatedDays,
      },
    });
  };

  return (
    <>
      {!editing && (
        <GridBoxPlannedBreaks className={`spec-planned-break-${id}`} data-testid="planned-break">
          <StyledText className="spec-planned-break-name" data-testid="planned-break-name">
            <TruncatedText fullWidth>{name}</TruncatedText>
          </StyledText>
          <StyledText data-testid="planned-break-lines">{lineText}</StyledText>
          <StyledText data-testid="planned-break-days">{days.map(displayNameForDay).join(", ")}</StyledText>
          <StyledText data-testid="planned-break-start-time">{formattedStartTime}</StyledText>
          <StyledText data-testid="planned-break-end-time">{formattedEndTime}</StyledText>
          <Box paddingTop="x1">
            <IconicButton icon="edit" onClick={() => setEditing(true)} type="button">
              Edit
            </IconicButton>
          </Box>
          <Box paddingTop="x1">
            <IconicButton icon="delete" onClick={() => onDelete(plannedBreak)} type="button">
              Delete
            </IconicButton>
          </Box>
        </GridBoxPlannedBreaks>
      )}
      {editing && (
        <FormWithInlineErrors
          initialValues={{
            days,
            endTime: formattedEndTime,
            id,
            lineIds,
            name,
            startTime: formattedStartTime,
          }}
          onSave={handleSave}
        >
          {({ dirty, handleReset }) => (
            <EditablePlannedBreakRow
              dirty={dirty}
              onCancel={() => handleCancel(handleReset)}
              testId="planned-break-row-editing"
            />
          )}
        </FormWithInlineErrors>
      )}
    </>
  );
}
